import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import {
  GroupReturnConditions,
  KeepYourItemRule,
  MANUAL_REVIEW_FLOW,
  MAX_REVIEW_AUTO_COMPLETE_DAYS,
  Requirement,
  ReviewAutoComplete,
  START_RETURN_ON_PLATFORM_TIMES,
  invalidDayFields,
  invalidRefundPercentages,
  returnPeriodRuleComplete
} from '@/domain/return-conditions'
import { MAX_RETURN_PERIOD_DAYS } from '@/domain/return-conditions'
import { RuleContext, whensComplete } from '@/domain/warehouses'
import {
  Choice,
  FormProps,
  SettingsPanel,
  SettingsSection,
  SwitchRow
} from '@/ui/components/group-settings'
import { ReturnWindowField } from './return-window/return-window-field'
import { RuleTable } from './rule-table'
import { useSeesReverOnly } from './use-sees-rever-only'

const { FieldDescription, FieldError, FieldLabel, FieldRoot, Input } = Base

type ConditionsFormProps = FormProps<GroupReturnConditions>

const DaysField: React.FC<{
  label: string
  description: string
  min?: number
  value: number
  error?: string
  readOnly: boolean
  onChange: (value: number) => void
}> = ({ label, description, min = 1, value, error, readOnly, onChange }) => (
  <FieldRoot invalid={!!error} className="max-w-md">
    <FieldLabel>{label}</FieldLabel>
    <FieldDescription>{description}</FieldDescription>
    <Input
      type="number"
      min={min}
      disabled={readOnly}
      value={value}
      onChange={(e) => onChange(Number(e.currentTarget.value))}
    />
    {error ? <FieldError match>{error}</FieldError> : null}
  </FieldRoot>
)

export const useConditionsProblem = () => {
  const { t } = useTranslation()
  return (conditions: GroupReturnConditions) => {
    if (!conditions.returnPeriodRules.every(returnPeriodRuleComplete))
      return t('returns_refunds.return_window.incomplete')
    if (invalidDayFields(conditions).length)
      return t('returns_refunds.conditions.days_problem')
    if (invalidRefundPercentages(conditions).length)
      return t('returns_refunds.keep_your_item.percentage_problem')
    const rules = [
      ...conditions.payForLogistics.rules,
      ...conditions.requireAuthorization.rules,
      ...conditions.uploadDocuments.rules,
      ...conditions.keepYourItem.rules
    ]
    return rules.every((rule) => whensComplete(rule.whens))
      ? undefined
      : t('returns_refunds.incomplete_rules')
  }
}

export const ReturnConditionsForm: React.FC<ConditionsFormProps> = ({
  value,
  saved,
  readOnly,
  onChange
}) => {
  const { t } = useTranslation()
  const seesReverOnly = useSeesReverOnly()
  const invalid = invalidDayFields(value)
  const set =
    <K extends keyof GroupReturnConditions>(key: K) =>
    (next: GroupReturnConditions[K]) =>
      onChange({ ...value, [key]: next })
  const dayError = (field: (typeof invalid)[number], max?: number) =>
    invalid.includes(field)
      ? max !== undefined && value[field] > max
        ? t('return_conditions.days_max_error', { max })
        : t('return_conditions.days_error')
      : undefined
  const manual = value.reviewFlow === MANUAL_REVIEW_FLOW
  return (
    <SettingsSection
      title={t('returns_refunds.conditions.title')}
      description={t('returns_refunds.conditions.description')}
    >
      <SettingsPanel>
        <ReturnWindowField
          rules={value.returnPeriodRules}
          saved={saved.returnPeriodRules}
          days={value.returnPeriodDays}
          daysError={dayError('returnPeriodDays', MAX_RETURN_PERIOD_DAYS)}
          editable={!readOnly}
          onChange={set('returnPeriodRules')}
          onDaysChange={set('returnPeriodDays')}
        />
        <DaysField
          label={t('return_conditions.days_to_ship_package')}
          description={t('return_conditions.days_to_ship_package_description')}
          min={0}
          value={value.maxDaysToSendPackage}
          error={
            invalid.includes('maxDaysToSendPackage')
              ? t('returns_refunds.conditions.days_to_ship_error')
              : undefined
          }
          readOnly={readOnly}
          onChange={set('maxDaysToSendPackage')}
        />
        <Choice<ReviewAutoComplete>
          title
          label={t('return_conditions.auto_complete')}
          description={t(
            manual
              ? 'return_conditions.auto_complete_description'
              : 'return_conditions.auto_complete_auto_review'
          )}
          value={value.reviewAutoComplete}
          options={[
            { value: 'OFF', label: t('return_conditions.auto_complete_off') },
            {
              value: 'MISSING',
              label: t('return_conditions.auto_complete_missing'),
              description: manual
                ? t('return_conditions.auto_complete_missing_description')
                : undefined
            },
            {
              value: 'APPROVED',
              label: t('return_conditions.auto_complete_approved'),
              description: manual
                ? t('return_conditions.auto_complete_approved_description')
                : undefined
            }
          ]}
          // Only a manual review can be left undone; an automatic one has nothing to complete.
          readOnly={readOnly || !manual}
          onChange={set('reviewAutoComplete')}
          extra={
            value.reviewAutoComplete !== 'OFF' ? (
              <FieldRoot
                invalid={invalid.includes('reviewAutoCompleteDays')}
                className="flex-row items-center gap-1.5"
              >
                <span className="text-xs text-neutral-600">
                  {t('return_conditions.auto_complete_days')}
                </span>
                <Input
                  size="sm"
                  type="number"
                  min={1}
                  max={MAX_REVIEW_AUTO_COMPLETE_DAYS}
                  className="w-16"
                  aria-label={t('return_conditions.auto_complete_days')}
                  disabled={readOnly || !manual}
                  value={value.reviewAutoCompleteDays}
                  onChange={(e) =>
                    set('reviewAutoCompleteDays')(Number(e.currentTarget.value))
                  }
                />
                <span className="text-xs text-neutral-600">
                  {t('return_conditions.auto_complete_days_unit')}
                </span>
              </FieldRoot>
            ) : null
          }
        />
      </SettingsPanel>
      <SettingsPanel>
        <Choice
          title
          label={t('returns_refunds.conditions.review_flow')}
          description={t('returns_refunds.conditions.review_flow_description')}
          value={manual ? MANUAL_REVIEW_FLOW : 'AUTO'}
          options={[
            {
              value: 'AUTO',
              label: t('returns_refunds.conditions.review_flow_auto')
            },
            {
              value: MANUAL_REVIEW_FLOW,
              label: t('returns_refunds.conditions.review_flow_manual')
            }
          ]}
          readOnly={readOnly}
          onChange={set('reviewFlow')}
        />
        <Choice
          title
          label={t('returns_refunds.conditions.start_on_platform')}
          description={t(
            'returns_refunds.conditions.start_on_platform_description'
          )}
          value={value.startReturnOnPlatformTime}
          options={START_RETURN_ON_PLATFORM_TIMES.map((time) => ({
            value: time,
            label: t(`returns_refunds.conditions.start_on_platform_${time}`)
          }))}
          readOnly={readOnly}
          onChange={set('startReturnOnPlatformTime')}
        />
        <SwitchRow
          label={t('returns_refunds.conditions.mark_unreviewed_missing')}
          description={t(
            'returns_refunds.conditions.mark_unreviewed_missing_description'
          )}
          checked={value.markUnreviewedItemsAsMissing}
          readOnly={readOnly}
          onChange={set('markUnreviewedItemsAsMissing')}
        />
        <SwitchRow
          label={t('returns_refunds.conditions.payments_enabled')}
          description={t(
            'returns_refunds.conditions.payments_enabled_description'
          )}
          checked={value.paymentsEnabled}
          readOnly={readOnly}
          onChange={set('paymentsEnabled')}
        />
        {/* settings-api lets only REVER change fraud detection. */}
        {seesReverOnly ? (
          <SwitchRow
            reverOnly
            label={t('returns_refunds.conditions.skip_fraud_detection')}
            description={t(
              'returns_refunds.conditions.skip_fraud_detection_description'
            )}
            checked={value.skipFraudDetection}
            readOnly={readOnly}
            onChange={set('skipFraudDetection')}
          />
        ) : null}
      </SettingsPanel>
    </SettingsSection>
  )
}

const REQUIREMENTS: {
  key: 'payForLogistics' | 'requireAuthorization' | 'uploadDocuments'
  context: RuleContext
}[] = [
  { key: 'uploadDocuments', context: 'UploadDocuments' },
  { key: 'requireAuthorization', context: 'RequireAuthorization' },
  { key: 'payForLogistics', context: 'PayForLogistics' }
]

export const RequirementsForm: React.FC<ConditionsFormProps> = ({
  value,
  saved,
  readOnly,
  onChange
}) => {
  const { t } = useTranslation()
  const keep = value.keepYourItem
  const badPercentages = invalidRefundPercentages(value)
  const setKeep = (next: Partial<GroupReturnConditions['keepYourItem']>) =>
    onChange({ ...value, keepYourItem: { ...keep, ...next } })
  return (
    <>
      <SettingsSection
        title={t('returns_refunds.requirements.title')}
        description={t('returns_refunds.requirements.description')}
      >
        {REQUIREMENTS.map(({ key, context }) => {
          const requirement: Requirement = value[key]
          const set = (next: Requirement) => onChange({ ...value, [key]: next })
          const title = t(`returns_refunds.requirements.${key}`)
          return (
            <SettingsPanel key={key} className="gap-3">
              <SwitchRow
                label={title}
                description={`${t(`returns_refunds.requirements.${key}_description`)} ${
                  requirement.always
                    ? t('returns_refunds.requirements.always_on')
                    : t('returns_refunds.requirements.always_off')
                }`}
                checked={requirement.always}
                readOnly={readOnly}
                onChange={(always) => set({ ...requirement, always })}
              />
              {requirement.always ? null : (
                <RuleTable
                  label={title}
                  rules={requirement.rules}
                  saved={saved[key].rules}
                  context={context}
                  readOnly={readOnly}
                  addLabel={t('returns_refunds.requirements.add_row')}
                  emptyText={t('returns_refunds.requirements.no_rows')}
                  newRule={() => ({ whens: [] })}
                  onChange={(rules) => set({ ...requirement, rules })}
                />
              )}
            </SettingsPanel>
          )
        })}
      </SettingsSection>
      <SettingsSection
        title={t('returns_refunds.keep_your_item.title')}
        description={t('returns_refunds.keep_your_item.description')}
      >
        <SettingsPanel className="gap-3">
          <SwitchRow
            label={t('returns_refunds.keep_your_item.enabled')}
            description={t(
              'returns_refunds.keep_your_item.enabled_description'
            )}
            checked={keep.enabled}
            readOnly={readOnly}
            onChange={(enabled) => setKeep({ enabled })}
          />
          {keep.enabled ? (
            <RuleTable<KeepYourItemRule>
              label={t('returns_refunds.keep_your_item.title')}
              rules={keep.rules}
              saved={saved.keepYourItem.rules}
              context="KeepYourItemRefundOption"
              readOnly={readOnly}
              addLabel={t('returns_refunds.keep_your_item.add_option')}
              emptyText={t('returns_refunds.keep_your_item.no_options')}
              newRule={() => ({
                whens: [],
                refundPercentage: 100,
                force: false
              })}
              columns={[
                {
                  label: t('returns_refunds.keep_your_item.refund'),
                  className: 'flex w-24 items-center gap-1.5',
                  render: (rule, update, { n }) => (
                    <FieldRoot
                      invalid={badPercentages.includes(n - 1)}
                      className="flex-row items-center gap-1.5"
                    >
                      <Input
                        size="sm"
                        type="number"
                        min={0}
                        max={100}
                        className="w-16"
                        aria-label={t(
                          'returns_refunds.keep_your_item.refund_for',
                          {
                            n
                          }
                        )}
                        disabled={readOnly}
                        value={rule.refundPercentage}
                        onChange={(e) =>
                          update({
                            ...rule,
                            refundPercentage: Number(e.currentTarget.value)
                          })
                        }
                      />
                      <span className="text-xs text-neutral-600">%</span>
                    </FieldRoot>
                  )
                },
                {
                  label: t('returns_refunds.keep_your_item.force'),
                  className: 'flex w-12 justify-center',
                  render: (rule, update, { n }) => (
                    <Base.Checkbox
                      aria-label={t(
                        'returns_refunds.keep_your_item.force_for',
                        {
                          n
                        }
                      )}
                      checked={rule.force}
                      disabled={readOnly}
                      onCheckedChange={(checked) =>
                        update({ ...rule, force: checked === true })
                      }
                    />
                  )
                }
              ]}
              onChange={(rules) => setKeep({ rules })}
            />
          ) : null}
        </SettingsPanel>
      </SettingsSection>
    </>
  )
}
